import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import api from '@/utils/api';
import { toast } from 'sonner';
import { LogOut, Shield, Video, Plus, Youtube, Edit, Eye, EyeOff, StickyNote, BarChart3, Lock } from 'lucide-react';
import MessagesNavButton from '@/components/MessagesNavButton';
import XVAssistWidget from '@/components/XVAssistWidget';
import PlayerOverviewSection from '@/components/PlayerOverviewSection';
import WellnessCheckInCard from '@/components/WellnessCheckInCard';
import ModuleStripCard from '@/components/ModuleStripCard';
import FullProfileForm from '@/components/FullProfileForm';

const SA_PROVINCES = ["Eastern Cape","Western Cape","Gauteng","KwaZulu-Natal","Limpopo","Mpumalanga","North West","Free State","Northern Cape"];

function CompletionBar({ completion, missing }) {
  return (
    <Card className="bg-zinc-900 border-zinc-800 p-6 mb-6" data-testid="completion-card">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-heading font-bold text-lg">Profile Completion</h3>
        <span className="text-2xl font-heading font-bold text-primary-neon">{completion}%</span>
      </div>
      <div className="w-full bg-zinc-800 rounded-full h-3 mb-4">
        <div className="bg-primary-neon h-3 rounded-full transition-all duration-500" style={{ width: `${completion}%` }} data-testid="completion-bar" />
      </div>
      {missing.length > 0 && (
        <div>
          <p className="text-zinc-400 text-xs mb-2">To improve your profile:</p>
          <ul className="space-y-1">
            {missing.map((m) => (
              <li key={m} className="text-zinc-500 text-xs flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full" />
                {m}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

export default function PlayerDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [videos, setVideos] = useState([]);
  const [completion, setCompletion] = useState({ completion: 0, missing: [] });
  const [notes, setNotes] = useState([]);
  const [consent, setConsent] = useState({ visible_to_scouts: true, parental_consent: false, parent_guardian_email: null });
  const [fullMeta, setFullMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [showVideoForm, setShowVideoForm] = useState(false);
  const [showVerificationForm, setShowVerificationForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [newNote, setNewNote] = useState('');

  const [profileData, setProfileData] = useState({
    gender: '', age: '', position: '', height: '', weight: '',
    school_club: '', province: '', town: '', bio: '', stats: {}
  });

  const [youtubeData, setYoutubeData] = useState({ title: '', description: '', video_type: 'highlight', video_url: '' });
  const [verificationData, setVerificationData] = useState({ coach_name: '', coach_email: '', school_club: '' });

  const fetchAll = useCallback(async () => {
    try {
      const [profRes, vidRes, compRes, notesRes, consentRes, fullRes] = await Promise.allSettled([
        api.get('/players/profile'),
        api.get('/players/videos'),
        api.get('/players/completion'),
        api.get('/players/notes'),
        api.get('/players/consent'),
        api.get('/player/full-profile')
      ]);
      if (fullRes.status === 'fulfilled') setFullMeta(fullRes.value.data);
      if (profRes.status === 'fulfilled') { setProfile(profRes.value.data); setProfileData(profRes.value.data); }
      else { setShowProfileForm(true); }
      if (vidRes.status === 'fulfilled') setVideos(vidRes.value.data);
      if (compRes.status === 'fulfilled') setCompletion(compRes.value.data);
      if (notesRes.status === 'fulfilled') setNotes(notesRes.value.data);
      if (consentRes.status === 'fulfilled') setConsent(consentRes.value.data);
    } catch (e) { console.error('Failed to load player dashboard data', e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreateProfile = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/players/profile', {
        ...profileData, age: parseInt(profileData.age),
        height: parseInt(profileData.height), weight: parseInt(profileData.weight)
      });
      setProfile(res.data); setShowProfileForm(false);
      toast.success('Profile created!'); fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create profile'); }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      const res = await api.put('/players/profile', {
        gender: profileData.gender, age: parseInt(profileData.age),
        position: profileData.position, height: parseInt(profileData.height),
        weight: parseInt(profileData.weight), school_club: profileData.school_club,
        province: profileData.province, town: profileData.town, bio: profileData.bio
      });
      setProfile(res.data); setShowEditForm(false);
      toast.success('Profile updated!'); fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update profile'); }
  };

  const handleAddYoutubeVideo = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/players/videos/youtube', youtubeData);
      setVideos([...videos, res.data]); setShowVideoForm(false);
      setYoutubeData({ title: '', description: '', video_type: 'highlight', video_url: '' });
      toast.success('Video added!'); fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add video'); }
  };

  const handleRequestVerification = async (e) => {
    e.preventDefault();
    try {
      await api.post('/verifications/request', verificationData);
      setShowVerificationForm(false);
      setVerificationData({ coach_name: '', coach_email: '', school_club: '' });
      toast.success('Verification request submitted!');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to request verification'); }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      await api.post(`/players/notes?note=${encodeURIComponent(newNote)}`);
      setNewNote(''); toast.success('Note added');
      const notesRes = await api.get('/players/notes');
      setNotes(notesRes.data);
    } catch (error) { toast.error('Failed to add note'); }
  };

  const handleConsentToggle = async (field, value) => {
    try {
      await api.put('/players/consent', { [field]: value });
      setConsent(prev => ({ ...prev, [field]: value }));
      toast.success('Privacy settings updated');
    } catch (error) { toast.error('Failed to update settings'); }
  };

  const extractYoutubeId = (url) => {
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/ ]{11})/);
    return match ? match[1] : null;
  };

  if (loading) {
    return <div className="min-h-screen bg-zinc-950 flex items-center justify-center"><div className="text-primary-neon text-xl">Loading...</div></div>;
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white" data-testid="player-dashboard">
      <nav className="border-b border-zinc-800 bg-zinc-900/50" data-testid="player-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="FutureXV" className="h-10" />
              <span className="text-zinc-400">|</span>
              <span className="font-heading font-bold">Player Dashboard</span>
            </div>
            <div className="flex items-center gap-2">
              <MessagesNavButton />
              <Button onClick={() => { logout(); navigate('/'); }} variant="ghost" className="text-zinc-400 hover:text-white" data-testid="logout-button">
                <LogOut className="w-4 h-4 mr-2" />Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-heading font-bold mb-2" data-testid="welcome-message">Welcome, {user.full_name}</h1>
          <p className="text-zinc-400 font-body">Manage your profile, showcase your talent, and control your data</p>
        </div>

        {fullMeta && ['pending', 'pending_verification', 'pending_dual_verification', 'declined'].includes(fullMeta.user_status) ? (
          <div className="space-y-6" data-testid="limited-access-mode">
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 text-zinc-400 text-sm">
              You currently have <span className="text-primary-neon font-bold">limited access</span>. Training programmes, PDS tracking and scout visibility unlock once your profile is approved.
            </div>
            <FullProfileForm onChanged={fetchAll} />
          </div>
        ) : !profile ? (
          <Card className="bg-zinc-900 border-zinc-800 p-8" data-testid="create-profile-card">
            <h2 className="text-2xl font-heading font-bold mb-6">Create Your Profile</h2>
            <form onSubmit={handleCreateProfile} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-zinc-300">Gender</Label>
                  <Select value={profileData.gender} onValueChange={(v) => setProfileData({...profileData, gender: v})} required>
                    <SelectTrigger data-testid="gender-select" className="bg-zinc-950 border-zinc-800 text-white mt-2 h-12"><SelectValue placeholder="Select gender" /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-zinc-300">Age</Label><Input data-testid="age-input" type="number" value={profileData.age} onChange={(e) => setProfileData({...profileData, age: e.target.value})} className="bg-zinc-950 border-zinc-800 text-white mt-2 h-12" required /></div>
                <div><Label className="text-zinc-300">Position</Label><Input data-testid="position-input" value={profileData.position} onChange={(e) => setProfileData({...profileData, position: e.target.value})} className="bg-zinc-950 border-zinc-800 text-white mt-2 h-12" placeholder="e.g., Fly-half" required /></div>
                <div><Label className="text-zinc-300">Height (cm)</Label><Input data-testid="height-input" type="number" value={profileData.height} onChange={(e) => setProfileData({...profileData, height: e.target.value})} className="bg-zinc-950 border-zinc-800 text-white mt-2 h-12" required /></div>
                <div><Label className="text-zinc-300">Weight (kg)</Label><Input data-testid="weight-input" type="number" value={profileData.weight} onChange={(e) => setProfileData({...profileData, weight: e.target.value})} className="bg-zinc-950 border-zinc-800 text-white mt-2 h-12" required /></div>
                <div><Label className="text-zinc-300">School / Club</Label><Input data-testid="school-input" value={profileData.school_club} onChange={(e) => setProfileData({...profileData, school_club: e.target.value})} className="bg-zinc-950 border-zinc-800 text-white mt-2 h-12" required /></div>
                <div>
                  <Label className="text-zinc-300">Province</Label>
                  <Select value={profileData.province} onValueChange={(v) => setProfileData({...profileData, province: v})}>
                    <SelectTrigger data-testid="province-select" className="bg-zinc-950 border-zinc-800 text-white mt-2 h-12"><SelectValue placeholder="Select province" /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                      {SA_PROVINCES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-zinc-300">Town</Label><Input data-testid="town-input" value={profileData.town} onChange={(e) => setProfileData({...profileData, town: e.target.value})} className="bg-zinc-950 border-zinc-800 text-white mt-2 h-12" required /></div>
              </div>
              <div><Label className="text-zinc-300">Bio</Label><Textarea data-testid="bio-input" value={profileData.bio} onChange={(e) => setProfileData({...profileData, bio: e.target.value})} className="bg-zinc-950 border-zinc-800 text-white mt-2 min-h-[100px]" placeholder="Tell scouts about yourself..." /></div>
              <Button type="submit" className="btn-primary px-8 py-6" data-testid="create-profile-button">Create Profile</Button>
            </form>
          </Card>
        ) : (
          <div className="space-y-6">
            <ModuleStripCard role="player" />
            <WellnessCheckInCard />
            <PlayerOverviewSection />
            <CompletionBar completion={completion.completion} missing={completion.missing} />

            <Tabs defaultValue="profile" className="w-full">
              <TabsList className="grid w-full grid-cols-5 mb-6 bg-zinc-900 max-w-2xl" data-testid="player-tabs">
                <TabsTrigger value="profile" className="data-[state=active]:bg-primary-neon data-[state=active]:text-black">Profile</TabsTrigger>
                <TabsTrigger value="testing" className="data-[state=active]:bg-primary-neon data-[state=active]:text-black" data-testid="testing-tab">Testing</TabsTrigger>
                <TabsTrigger value="videos" className="data-[state=active]:bg-primary-neon data-[state=active]:text-black">Videos</TabsTrigger>
                <TabsTrigger value="notes" className="data-[state=active]:bg-primary-neon data-[state=active]:text-black">Notes</TabsTrigger>
                <TabsTrigger value="privacy" className="data-[state=active]:bg-primary-neon data-[state=active]:text-black">Privacy</TabsTrigger>
              </TabsList>

              <TabsContent value="profile">
                <Card className="bg-zinc-900 border-zinc-800 p-8" data-testid="profile-card">
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h2 className="text-2xl font-heading font-bold mb-2">Your Profile</h2>
                      <div className="flex gap-2">
                        {profile.verified && (<Badge className="bg-blue-500/10 text-blue-400 border border-blue-500/20" data-testid="verified-badge"><Shield className="w-3 h-3 mr-1" />Verified</Badge>)}
                        {consent.visible_to_scouts ? (<Badge className="bg-green-500/10 text-green-400 border border-green-500/20"><Eye className="w-3 h-3 mr-1" />Visible</Badge>) : (<Badge className="bg-red-500/10 text-red-400 border border-red-500/20"><EyeOff className="w-3 h-3 mr-1" />Hidden</Badge>)}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => { setProfileData(profile); setShowEditForm(true); }} variant="outline" className="border-zinc-700 text-zinc-300 hover:text-white" data-testid="edit-profile-button"><Edit className="w-4 h-4 mr-2" />Edit</Button>
                      {!profile.verified && (
                        <Dialog open={showVerificationForm} onOpenChange={setShowVerificationForm}>
                          <DialogTrigger asChild>
                            <Button className="btn-primary" data-testid="request-verification-button"><Shield className="w-4 h-4 mr-2" />Request Verification</Button>
                          </DialogTrigger>
                          <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
                            <DialogHeader><DialogTitle className="font-heading">Request Verification</DialogTitle></DialogHeader>
                            <form onSubmit={handleRequestVerification} className="space-y-4">
                              <div><Label>Coach Name</Label><Input data-testid="coach-name-input" value={verificationData.coach_name} onChange={(e) => setVerificationData({...verificationData, coach_name: e.target.value})} className="bg-zinc-950 border-zinc-800 text-white mt-2" required /></div>
                              <div><Label>Coach Email</Label><Input data-testid="coach-email-input" type="email" value={verificationData.coach_email} onChange={(e) => setVerificationData({...verificationData, coach_email: e.target.value})} className="bg-zinc-950 border-zinc-800 text-white mt-2" required /></div>
                              <div><Label>School / Club</Label><Input data-testid="verification-school-input" value={verificationData.school_club} onChange={(e) => setVerificationData({...verificationData, school_club: e.target.value})} className="bg-zinc-950 border-zinc-800 text-white mt-2" required /></div>
                              <Button type="submit" className="btn-primary w-full" data-testid="submit-verification-button">Submit Request</Button>
                            </form>
                          </DialogContent>
                        </Dialog>
                      )}
                    </div>
                  </div>

                  {showEditForm ? (
                    <form onSubmit={handleUpdateProfile} className="space-y-6" data-testid="edit-profile-form">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <Label className="text-zinc-300">Gender</Label>
                          <Select value={profileData.gender} onValueChange={(v) => setProfileData({...profileData, gender: v})}>
                            <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white mt-2 h-12"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                              <SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div><Label className="text-zinc-300">Age</Label><Input type="number" value={profileData.age} onChange={(e) => setProfileData({...profileData, age: e.target.value})} className="bg-zinc-950 border-zinc-800 text-white mt-2 h-12" /></div>
                        <div><Label className="text-zinc-300">Position</Label><Input value={profileData.position} onChange={(e) => setProfileData({...profileData, position: e.target.value})} className="bg-zinc-950 border-zinc-800 text-white mt-2 h-12" /></div>
                        <div><Label className="text-zinc-300">Height (cm)</Label><Input type="number" value={profileData.height} onChange={(e) => setProfileData({...profileData, height: e.target.value})} className="bg-zinc-950 border-zinc-800 text-white mt-2 h-12" /></div>
                        <div><Label className="text-zinc-300">Weight (kg)</Label><Input type="number" value={profileData.weight} onChange={(e) => setProfileData({...profileData, weight: e.target.value})} className="bg-zinc-950 border-zinc-800 text-white mt-2 h-12" /></div>
                        <div><Label className="text-zinc-300">School / Club</Label><Input value={profileData.school_club} onChange={(e) => setProfileData({...profileData, school_club: e.target.value})} className="bg-zinc-950 border-zinc-800 text-white mt-2 h-12" /></div>
                        <div>
                          <Label className="text-zinc-300">Province</Label>
                          <Select value={profileData.province} onValueChange={(v) => setProfileData({...profileData, province: v})}>
                            <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white mt-2 h-12"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800 text-white">{SA_PROVINCES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div><Label className="text-zinc-300">Town</Label><Input value={profileData.town} onChange={(e) => setProfileData({...profileData, town: e.target.value})} className="bg-zinc-950 border-zinc-800 text-white mt-2 h-12" /></div>
                      </div>
                      <div><Label className="text-zinc-300">Bio</Label><Textarea value={profileData.bio || ''} onChange={(e) => setProfileData({...profileData, bio: e.target.value})} className="bg-zinc-950 border-zinc-800 text-white mt-2 min-h-[100px]" /></div>
                      <div className="flex gap-3">
                        <Button type="submit" className="btn-primary px-8" data-testid="save-profile-button">Save Changes</Button>
                        <Button type="button" variant="outline" onClick={() => setShowEditForm(false)} className="border-zinc-700 text-zinc-400">Cancel</Button>
                      </div>
                    </form>
                  ) : (
                    <>
                      <div className="grid md:grid-cols-3 gap-6">
                        {[{l:'Gender',v:profile.gender,c:true},{l:'Age',v:profile.age},{l:'Position',v:profile.position},{l:'Height',v:`${profile.height} cm`},{l:'Weight',v:`${profile.weight} kg`},{l:'School/Club',v:profile.school_club},{l:'Province',v:profile.province},{l:'Town',v:profile.town}].map((item) => (
                          <div key={item.l} className="player-stat-card rounded-lg p-4">
                            <p className="text-zinc-500 text-sm mb-1">{item.l}</p>
                            <p className={`text-xl font-heading font-bold ${item.c ? 'capitalize' : ''}`}>{item.v}</p>
                          </div>
                        ))}
                      </div>
                      {profile.bio && (<div className="mt-6"><p className="text-zinc-500 text-sm mb-2">Bio</p><p className="text-zinc-300">{profile.bio}</p></div>)}
                    </>
                  )}
                </Card>
              </TabsContent>

              <TabsContent value="testing">
                <FullProfileForm onChanged={fetchAll} />
              </TabsContent>

              <TabsContent value="videos">
                <Card className="bg-zinc-900 border-zinc-800 p-8" data-testid="videos-card">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-heading font-bold">Match Videos</h2>
                    <Dialog open={showVideoForm} onOpenChange={setShowVideoForm}>
                      <DialogTrigger asChild><Button className="btn-primary" data-testid="add-video-button"><Plus className="w-4 h-4 mr-2" />Add Video</Button></DialogTrigger>
                      <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
                        <DialogHeader><DialogTitle className="font-heading">Add Match Video</DialogTitle></DialogHeader>
                        <form onSubmit={handleAddYoutubeVideo} className="space-y-4">
                          <div><Label>Title</Label><Input data-testid="video-title-input" value={youtubeData.title} onChange={(e) => setYoutubeData({...youtubeData, title: e.target.value})} className="bg-zinc-950 border-zinc-800 text-white mt-2" required /></div>
                          <div><Label>Description</Label><Textarea data-testid="video-description-input" value={youtubeData.description} onChange={(e) => setYoutubeData({...youtubeData, description: e.target.value})} className="bg-zinc-950 border-zinc-800 text-white mt-2" /></div>
                          <div>
                            <Label>Video Type</Label>
                            <Select value={youtubeData.video_type} onValueChange={(v) => setYoutubeData({...youtubeData, video_type: v})}>
                              <SelectTrigger data-testid="video-type-select" className="bg-zinc-950 border-zinc-800 text-white mt-2"><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                                <SelectItem value="highlight">Highlight Reel</SelectItem><SelectItem value="full_match">Full Match</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div><Label>YouTube URL</Label><Input data-testid="youtube-url-input" value={youtubeData.video_url} onChange={(e) => setYoutubeData({...youtubeData, video_url: e.target.value})} className="bg-zinc-950 border-zinc-800 text-white mt-2" placeholder="https://www.youtube.com/watch?v=..." required /></div>
                          <Button type="submit" className="btn-primary w-full" data-testid="submit-video-button">Add Video</Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                  {videos.length === 0 ? (
                    <div className="text-center py-12 text-zinc-500" data-testid="no-videos-message"><Video className="w-12 h-12 mx-auto mb-4 opacity-50" /><p>No videos yet. Add your match footage to showcase your skills!</p></div>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-6">
                      {videos.map((video) => {
                        const videoId = extractYoutubeId(video.video_url);
                        return (
                          <div key={video.id} className="bg-zinc-950 rounded-lg overflow-hidden border border-zinc-800" data-testid={`video-${video.id}`}>
                            {videoId ? (<div className="video-container"><iframe src={`https://www.youtube.com/embed/${videoId}`} title={video.title} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe></div>) : (<div className="aspect-video bg-zinc-900 flex items-center justify-center"><Youtube className="w-12 h-12 text-zinc-700" /></div>)}
                            <div className="p-4">
                              <h3 className="font-heading font-bold text-lg mb-1">{video.title}</h3>
                              {video.description && <p className="text-zinc-400 text-sm">{video.description}</p>}
                              <Badge className="mt-2 bg-primary-neon/10 text-primary-neon border-0">{video.video_type === 'highlight' ? 'Highlight Reel' : 'Full Match'}</Badge>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card>
              </TabsContent>

              <TabsContent value="notes">
                <Card className="bg-zinc-900 border-zinc-800 p-8" data-testid="notes-card">
                  <h2 className="text-2xl font-heading font-bold mb-6">Development Notes</h2>
                  <div className="flex gap-3 mb-6">
                    <Input value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Add a training note, goal, or reflection..." className="bg-zinc-950 border-zinc-800 text-white flex-1" data-testid="note-input" />
                    <Button onClick={handleAddNote} className="btn-primary" data-testid="add-note-button"><Plus className="w-4 h-4 mr-2" />Add</Button>
                  </div>
                  {notes.length === 0 ? (
                    <div className="text-center py-12 text-zinc-500"><StickyNote className="w-12 h-12 mx-auto mb-4 opacity-50" /><p>No development notes yet. Track your progress!</p></div>
                  ) : (
                    <div className="space-y-3">
                      {notes.map((n) => (
                        <div key={n.id} className="bg-zinc-950 border border-zinc-800 rounded-lg p-4" data-testid={`note-${n.id}`}>
                          <p className="text-zinc-200">{n.note}</p>
                          <p className="text-zinc-600 text-xs mt-2">{new Date(n.created_at).toLocaleDateString()}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </TabsContent>

              <TabsContent value="privacy">
                <Card className="bg-zinc-900 border-zinc-800 p-8" data-testid="privacy-card">
                  <div className="flex items-center gap-3 mb-6">
                    <Lock className="w-6 h-6 text-primary-neon" />
                    <h2 className="text-2xl font-heading font-bold">Privacy & POPIA Controls</h2>
                  </div>
                  <p className="text-zinc-400 mb-8">In compliance with South Africa's Protection of Personal Information Act (POPIA), you have full control over your data visibility.</p>
                  <div className="space-y-6">
                    <div className="flex items-center justify-between p-5 bg-zinc-950 rounded-lg border border-zinc-800" data-testid="visibility-toggle">
                      <div>
                        <h3 className="font-heading font-bold">Visible to Scouts</h3>
                        <p className="text-zinc-400 text-sm">When enabled, your profile appears in scout searches. When disabled, your profile is hidden.</p>
                      </div>
                      <Switch checked={consent.visible_to_scouts} onCheckedChange={(checked) => handleConsentToggle('visible_to_scouts', checked)} className="data-[state=checked]:bg-primary-neon" data-testid="visibility-switch" />
                    </div>
                    <div className="flex items-center justify-between p-5 bg-zinc-950 rounded-lg border border-zinc-800" data-testid="parental-consent-toggle">
                      <div>
                        <h3 className="font-heading font-bold">Parental/Guardian Consent</h3>
                        <p className="text-zinc-400 text-sm">Required for players under 18. Confirms a parent or guardian has given consent for this profile.</p>
                      </div>
                      <Switch checked={consent.parental_consent} onCheckedChange={(checked) => handleConsentToggle('parental_consent', checked)} className="data-[state=checked]:bg-primary-neon" data-testid="parental-consent-switch" />
                    </div>
                    {consent.parental_consent && (
                      <div className="p-5 bg-zinc-950 rounded-lg border border-zinc-800">
                        <Label className="text-zinc-300">Parent/Guardian Email</Label>
                        <div className="flex gap-3 mt-2">
                          <Input value={consent.parent_guardian_email || ''} onChange={(e) => setConsent({...consent, parent_guardian_email: e.target.value})} placeholder="parent@email.com" className="bg-zinc-900 border-zinc-800 text-white flex-1" data-testid="parent-email-input" />
                          <Button onClick={() => handleConsentToggle('parent_guardian_email', consent.parent_guardian_email)} className="btn-primary" data-testid="save-parent-email-button">Save</Button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="mt-8 p-5 bg-zinc-950 rounded-lg border border-zinc-800">
                    <h3 className="font-heading font-bold mb-2">Your Data Rights (POPIA)</h3>
                    <ul className="space-y-2 text-zinc-400 text-sm">
                      <li className="flex items-start gap-2"><div className="w-1.5 h-1.5 bg-primary-neon rounded-full mt-1.5 flex-shrink-0" />You have the right to access all personal data we hold about you</li>
                      <li className="flex items-start gap-2"><div className="w-1.5 h-1.5 bg-primary-neon rounded-full mt-1.5 flex-shrink-0" />You can request correction of any inaccurate data</li>
                      <li className="flex items-start gap-2"><div className="w-1.5 h-1.5 bg-primary-neon rounded-full mt-1.5 flex-shrink-0" />You can request deletion of your profile and all associated data</li>
                      <li className="flex items-start gap-2"><div className="w-1.5 h-1.5 bg-primary-neon rounded-full mt-1.5 flex-shrink-0" />You can withdraw consent at any time by toggling visibility off</li>
                      <li className="flex items-start gap-2"><div className="w-1.5 h-1.5 bg-primary-neon rounded-full mt-1.5 flex-shrink-0" />Under-18 profiles require verified parental consent</li>
                    </ul>
                  </div>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>
      <XVAssistWidget />
    </div>
  );
}
